import React, { useEffect, useState, useCallback, useRef} from 'react';
import Navbar from '../components/Navbar'; // Adjust the import path as necessary
import { Box, Button, IconButton, Chip, Grid, Typography, Modal, Pagination, Menu, MenuItem, ListItemText } from '@mui/material'; // Added Menu and MenuItem
import AssistantCard from '../components/AssistantCard'; // Adjust the import path as necessary
import { Add, Close, Dashboard, Sort as SortIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material'; // Update imports to include new icons
import { useNavigate } from 'react-router-dom';
import api from "../api";
import AssistantTemplate from '../components/AssistantForms/AssistantTemplate';
import Loader from '../components/Loader';
import NoDataFound from '../components/NoDataFound';
import { fetchAssistantConfig } from './utils/fetchConfig';
import { capitalizeFirstLetter } from './utils/stringCapital';
import { useGetAllPermissions } from '../pages/utils/getAllPermissions';
import { ButtonSx, menuItemSx, menuSx, paginationSx, SecondaryChipSx } from '../styles/GlobalStyles';
import { Icon } from "@iconify/react";
import { useLocation } from 'react-router-dom';
import {constant} from '../components/Constants';
import { isUnderMaintenance } from "../pages/utils/maintenance";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const CONFIG = [
  'Cutoff',
  'EndConversationOnGoodbye',
  'FilterAudio',
  'IdealTime',
  'InitialMsg',
  'Interruption',
  'LLMModel',
  'LLMProvider',
  'MaxToken',
  'NumOfHumanCheck',
  'Prompt',
  'SpeedOfText',
  'Temperature',
  'Transcriber',
  'TranscriberLanguage',
  'TranscriberProvider',
  'Voice',
  'VoiceLanguage',
  'VoiceProvider'
];

const Assistants = () => {
  const [searchOption, setSearchOption] = React.useState('All');
  const [searchValue, setSearchValue] = React.useState('');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [templateModalOpen, setTemplateModalOpen] = React.useState(false); // New modal state
  const [token, setToken] = useState("");
  const [assistants, setAssistants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // Initialize with 1 as default
  const debounceTimeoutRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [defaultData, setDefaultData] = useState({})
  const [templates, setTemplates] = useState([]); // New state for templates
  const { permissions, getAllPermissions } = useGetAllPermissions();
  const searchInputRef = useRef(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true); // Add this new state
  const canFetchAssistant = permissions?.includes("fetch_assistant") ?? false;
  const canCreateAssistant = permissions?.includes("create_assistant") ?? false;
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortField, setSortField] = useState('date'); // New state for sort field (date, alphabetical, relevance)
  const [sortDirection, setSortDirection] = useState('desc'); // New state for direction (asc, desc)

  const navigate = useNavigate();
  const location = useLocation();

  const getCompanyIdFromUrl = () => {
    const match = location.pathname.match(/\/company\/([^/]+)/);
    return match ? match[1] : null;
  };

  const companyId = getCompanyIdFromUrl();
  const generateUrl = useCallback((path) => {
    // Make sure path is a string
    if (typeof path !== 'string') {
      console.warn('Invalid path provided to generateUrl:', path);
      path = '';
    }
    
    // Handle case where path doesn't start with '/'
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Return the appropriate URL
    return companyId ? `/company/${companyId}${normalizedPath}` : normalizedPath;
  }, [companyId]);
  useEffect(() => {
       getAllPermissions();
     }, [token, getAllPermissions]);
     
  const getDefaultConfig = async () => {
    try {
        await fetchAssistantConfig(CONFIG,(data) => {
          if (data) {
            setDefaultData(data)
          }
        });
    } catch (error) {
        console.error("Error getting default config:", error);
    }
  };
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const {data:{data}} = await api.get(`/v1/template/`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      if(data){
        setTemplates(data);
      }
    }  catch (error) {
      console.error('Error fetching assistant data:', error);
    }finally{
    setLoading(false)
    }
  };

  const handleOptionChange = (event) => {
    const newSearchOption = event.target.value;
    setSearchOption(newSearchOption);
    
    // If there's an active search, refresh results with new option
    if (searchValue.trim()) {
        // Clear previous timeout if any
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        // Set a new timeout to trigger API call
        debounceTimeoutRef.current = setTimeout(() => {
            fetchAllAssistants(1, searchValue, newSearchOption, false);
        }, 50);
    }
};

  const handleSearchChange = (event) => {
    const newSearchValue = event.target.value;
    setSearchValue(newSearchValue);

    if (searchInputRef.current) {
        searchInputRef.current.focus(); // Ensure input stays focused
    }

    // Clear previous timeout if any
    if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new timeout to trigger API call after delay
    debounceTimeoutRef.current = setTimeout(() => {
        fetchAllAssistants(1, newSearchValue, searchOption, false);
    }, 300); // Increased debounce time for better performance
};

  const searchOptions = [
    { value: 'Name', label: 'Name' },
    { value: 'Category', label: 'Category' }     // Make sure this is an allowed field in backend
  ];

  const handleOpenModal = () => {
    setModalOpen(true);
    getDefaultConfig()
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenTemplateModal = () => { // New function to open template modal
    setTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => { // New function to close template modal
    setTemplateModalOpen(false);
  };

  const getToken = () => {
    const t = JSON.parse(localStorage.getItem('token')).token;
    setToken(t);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortFieldSelect = (field) => {
    setSortField(field);
    handleSortClose();
  };

  const toggleSortDirection = () => {
    const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
    setSortDirection(newDirection);
  };

  const fetchAllAssistants = async (page = 1, searchValue = "", searchOption = "All", showLoading = true) => {
    if (showLoading) setLoading(true);
  
    const config = {
      headers: { "Authorization": `Bearer ${token}` }
    };
  
    // Map our frontend sort fields to backend sort fields
    let sortBy;
    switch(sortField) {
      case 'date':
        sortBy = 'UpdatedAt';
        break;
      case 'alphabetical':
        sortBy = 'Name';
        break;
      default:
        sortBy = 'UpdatedAt';
    }
  
    try {
      const response = await api.get(
        `/assistants?limit=${constant.DEFAULT_LIMIT}&search=${searchValue}&searchField=${searchOption}&page=${page}&sortBy=${sortBy}&sortOrder=${sortDirection}`,
        config
      );
      const data = response?.data?.data;
  
      if (data) {
        setAssistants(data.assistants || []);
        setTotalPages(data.totalPages || 1);
        setCurrentPage(data.currentPage || 1);
      } else {
        console.warn("Unexpected API response structure:", response);
      }
    } catch (error) {
      console.error("Error fetching assistants:", error);
      alert("Failed to fetch assistants. Please try again.");
    } finally {
      if (showLoading) setLoading(false);
      setIsInitialLoading(false);
    }
};

  const handlePageChange = (event, newPage) => {
    // Only fetch if the page is actually different
    if (newPage !== currentPage) {
      fetchAllAssistants(newPage, searchValue, searchOption);
    }
  };

  useEffect(() => {
    getToken();
    if (token) {
        fetchAllAssistants(1, searchValue, searchOption);
        fetchTemplates();
    }
  }, [token]);

  useEffect(() => {
    if (localStorage.getItem('sidebarOpen') === 'true') {
      setSidebarOpen(true);
    }

  }, [localStorage.getItem('sidebarOpen')])

  useEffect(() => {
    if (token && !isInitialLoading) {
      fetchAllAssistants(currentPage, searchValue, searchOption, false);
    }
  }, [sortField, sortDirection]);

    
    const [activeFilters, setActiveFilters] = useState([]);
    
    // Add a filter when a chip in an assistant card is clicked
    const addFilter = useCallback((filter) => {
      if (typeof filter !== 'string' || !filter.trim()) return;
      setActiveFilters((prevFilters) => (!prevFilters.includes(filter) ? [...prevFilters, filter] : prevFilters));
    }, []);
  
    // Remove a filter when the "X" on a filter chip is clicked
    const removeFilter = useCallback((filter) => {
      if (typeof filter !== 'string' || !filter.trim()) return;
      setActiveFilters((prevFilters) => prevFilters.filter((f) => f !== filter));
    }, []);
  
    // Clear all filters
    const clearFilters = useCallback(() => setActiveFilters([]), []);
  
    // Filter assistant cards dynamically
    const filteredTemplates = templates.filter(
      (template) =>
        activeFilters.length === 0 ||
        activeFilters.includes(template.type) ||
        activeFilters.includes(template.category)
    );
    

    if (isInitialLoading || loading) {
      return <Loader />;
    }

  return (
    <Box>
    {/* Top Navbar */}
    <Navbar
      title="Assistants"
      searchOptions={searchOptions}
      searchOption={searchOption}
      searchValue={searchValue}
      onSearchOptionChange={handleOptionChange}
      onSearchValueChange={handleSearchChange}
    />

    {/* Real-Time Voice Assistant Section */}
    <Box px={3} py={2} bgcolor="#F9FAFB">
      {/* Create Assistant Section */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          borderRadius: '1rem',
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          padding: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon icon="mingcute:ai-fill" width="52" height="52" />
            </Box>

            {/* Title & Subtitle */}
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                Real Time Voice Assistants
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Personalized AI Assistant Available For You 24/7
              </Typography>

              {/* Chips */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {[
                  { icon: <Icon icon="solar:incoming-call-bold" width="16" height="16" />, label: 'Inbound' },
                  { icon: <Icon icon="solar:outgoing-call-bold" width="16" height="16" />, label: 'Outbound' },
                  { icon: <Icon icon="zondicons:time" width="16" height="16"/>, label: '24/7 Uptime' },
                ].map((chip, i) => (
                  <Chip
                    key={i}
                    icon={React.cloneElement(chip.icon, { fontSize: 'small' })}
                    label={chip.label}
                    sx={SecondaryChipSx}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Right Button */}
          <Button
            onClick={handleOpenModal}
            variant="contained"
            sx={ButtonSx}
            startIcon={<Add />}
            disabled={isUnderMaintenance("create_assistant_button")}
            >
            Create Assistant
          </Button>
        </Box>
      </Box>

      {/* Assistants Container */}
      {canFetchAssistant && (
        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #E5E7EB',
            borderRadius: '1rem',
            p: 3,
            mt: 3
          }}
        >
          {/* Header */}
          <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#111827' }}>
                All Assistants
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                {canCreateAssistant
                  ? 'Browse your existing assistants and adjust their configurations to fit your requirements.'
                  : 'View your existing assistants.'}
              </Typography>
            </Box>
            
            {/* Sort Controls */}
            <Box display="flex" alignItems="center" gap={1}>
              {/* Sort Field Button */}
              <Button 
                onClick={handleSortClick}
                startIcon={<SortIcon />}
                variant="outlined"
                sx={{
                  color: '#111827',
                  borderColor: '#E5E7EB',
                  textTransform: 'none',
                  height: '36px',
                  '&:hover': {
                    borderColor: '#D1D5DB',
                    backgroundColor: '#F9FAFB',
                  }
                }}
              >
                {sortField === 'date' ? 'Date' : 
                 sortField === 'alphabetical' ? 'Alphabetical' : 'Sort'}
              </Button>
              
              {/* Direction Toggle Button */}
              <IconButton 
                onClick={toggleSortDirection}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  color: '#111827',
                  height: '36px',
                  width: '36px',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  }
                }}
              >
                {sortDirection === 'desc' ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />}
              </IconButton>
              
              {/* Sort Menu */}
              <Menu
                anchorEl={sortAnchorEl}
                open={Boolean(sortAnchorEl)}
                onClose={handleSortClose}
                PaperProps={{
                    elevation: 2,
                    sx: menuSx,
                    }}
              >
                <Typography sx={{ mb:1 ,fontWeight: 600, color: '#6B7280' }}>
                  Sort by
                </Typography>
                
                <MenuItem sx={menuItemSx} onClick={() => handleSortFieldSelect('date')} selected={sortField === 'date'}>
                  <ListItemText>Date</ListItemText>
                </MenuItem>
                
                <MenuItem sx={menuItemSx} onClick={() => handleSortFieldSelect('alphabetical')} selected={sortField === 'alphabetical'}>
                  <ListItemText>Alphabetical</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Empty State or Grid */}
          {!isInitialLoading && !loading && assistants.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', height: '70dvh' }}>
              <NoDataFound
                title="No Assistant Found"
                subtitle="There is no assistant found to show the data right now."
                buttonLabel="Create New Assistant"
                buttonLink={generateUrl('/assistants/create')}
              />
            </Box>
          ) : (
            <>
              {/* Assistants Grid */}
              <Grid container spacing={3}>
                {assistants.map((assistant) => (
                  <Grid item xs={12} sm={6} md={4} lg={4} key={assistant._id}>
                  <AssistantCard
                      name={assistant.Name}
                      category={assistant.Category}
                      description={assistant.Description || 'No description'}
                      Type={assistant.Type}
                      lastUpdated={assistant.UpdatedAt}
                      fileUrl={assistant.Logo?.fileUrl || 'defaultLogo.png'}
                      buttonAction={{ 
                        label: 'View Assistant', 
                        url: assistant._id ? generateUrl(`/assistant/${assistant._id}`) : '#' 
                      }}
                      assistant={assistant}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {assistants.length > 0 && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    sx={paginationSx}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>


    <Modal
      open={modalOpen}
      onClose={handleCloseModal}
      aria-labelledby="choose-preference-modal-title"
      aria-describedby="choose-preference-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "auto",
          backgroundColor: "#fff",
          borderRadius: "1rem",
          p: "2rem",
          gap: 2,
        }}
      >
        {/* Heading with Button */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            id="choose-preference-modal-title"
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "1.5rem", color: "#000" }}
          >
          Create Assistant
          </Typography>
          <IconButton onClick={handleCloseModal} sx={{ color: "#000" }}>
            <Close />
          </IconButton>
        </Box>

        {/* Main Heading: Choose Preference */}
        <Box sx={{ textAlign: "center", mb:"2rem", gap: 1 , display: "flex", flexDirection: "column", alignItems: "center"}}>
          <Typography
            id="choose-preference-modal-title"
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "1.5rem", color: "#000" }}
          >
            Choose Preference
          </Typography>
          <Typography
            id="choose-preference-modal-description"
            sx={{ fontSize: "1rem", color: "#444"}}
          >
            Pick your starting point for assistant
          </Typography>
        </Box>

        {/* Cards */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          {/* Template Library */}
          <Box
            onClick={
              isUnderMaintenance("template_library_button")
                ? undefined
                : handleOpenTemplateModal
            }
            sx={{
              width: 280,
              border: "1px solid #E5E7EB",
              borderRadius: "1rem",
              p: 3,
              cursor: isUnderMaintenance("template_library_button") ? "not-allowed" : "pointer",
              textAlign: "center",
              opacity: isUnderMaintenance("template_library_button") ? 0.5 : 1,
              pointerEvents: isUnderMaintenance("template_library_button") ? "none" : "auto",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "#000",
                color: "#fff",
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Dashboard fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: "1.1rem", color: "#000" }}>
              Template Library
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "#444", mt: 1 }}>
              Get started with predefined assistants
            </Typography>
          </Box>

          {/* Create from Scratch */}
          <Box
            onClick={
              isUnderMaintenance("create_from_scratch_button")
                ? undefined
                : () => {
                    navigate(generateUrl('/assistants/create'));
                    localStorage.removeItem('assistant');
                    localStorage.removeItem('KnowledgeBase');
                    localStorage.removeItem('Logo');
                    localStorage.setItem("assistant", JSON.stringify(defaultData));
                  }
            }
            sx={{
              width: 280,
              border: "1px solid #E5E7EB",
              borderRadius: "1rem",
              p: 3,
              cursor: isUnderMaintenance("create_from_scratch_button") ? "not-allowed" : "pointer",
              textAlign: "center",
              opacity: isUnderMaintenance("create_from_scratch_button") ? 0.5 : 1,
              pointerEvents: isUnderMaintenance("create_from_scratch_button") ? "none" : "auto",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "#000",
                color: "#fff",
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Add fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: "1.1rem", color: "#000" }}>
              Create from Scratch
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "#444", mt: 1 }}>
              Create an assistant from scratch
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>




      {/* Template Library Modal */}
      <Modal
        open={templateModalOpen}
        onClose={handleCloseTemplateModal}
        aria-labelledby="template-library-modal-title"
        aria-describedby="template-library-modal-description"
      >
        <Box
          sx={{
            ...style,
            width: "80dvw",
            height: "80dvh",
            padding: "2rem",
            borderRadius: "1rem",
            overflow: "auto",
          }}
        >
         
            
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor:'red'
              }}
            >

              <Box sx={{
                display:'flex',
                flexDirection:'column',
                alignItems:'flex-start',
                gap:0.5
              }}>
              <Typography variant="h5" id="template-library-modal-title">
                Template Library
              </Typography>
              <Typography sx={{fontSize:'1rem', color:'#7C7C83', fontWeight:'500'}} gutterBottom>
              Here you can choose from predefined assistants to help you get
              started.
            </Typography>
            </Box>

            <Button
                onClick={handleCloseTemplateModal}
                style={{ minWidth: "auto" }}
              >
                <Close sx={{
                  color:'black'
                }} />
              </Button>
            </Box>

                {/* Filters Section */}
                
                {activeFilters.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, marginBottom: 2, mt:2 }}>
                  {activeFilters.map((filter) => (
                    <Chip
                      key={filter}
                      label={capitalizeFirstLetter(filter)}
                      onDelete={() => removeFilter(filter)}
                      sx={{
                        py: 1,
                        pl:1,
                        fontSize: '14px',
                        fontWeight: '500',
                        '& .MuiChip-label': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    },
                        bgcolor: "#02042D",
                        color: "white",
                        "& .MuiChip-deleteIcon": { color: "white",ml:0.5 },
                      }}
                    />
                  ))}
                  <Button onClick={clearFilters} sx={{ textTransform: "none" }}>
                    Clear All
                  </Button>
                </Box>
              )}

          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(500px, 1fr))",
              gap: "1rem",
              margin: "1rem 0",
              marginTop: '1rem'
            }}
          >

              {filteredTemplates.map((template) => ( <AssistantTemplate 
                  key={template.id}
                  name={template.name}
                  type={template.type}
                  category={template.category}
                  description={template.description}
                  onChipClick={addFilter}
                  data={template}
                  setModalOpen={setModalOpen}
                />
              ))}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Assistants;
